import { MoreHorizontal, Pencil, Trash2, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface ViewerMenuProps {
  /** Named in the trigger's accessible label. */
  readonly prototypeName: string;
  /** Whether to offer "Upload a new version" (spec matrix). */
  readonly canUpload: boolean;
  /** Whether to offer edit and delete (spec matrix). */
  readonly canModify: boolean;
  readonly onEdit: () => void;
  readonly onUpload: () => void;
  readonly onDelete: () => void;
}

/**
 * The viewer's overflow menu (spec R3.3).
 *
 * Every item opens a dialog that already exists — `EditPrototypeDialog`,
 * `UploadVersionDialog`, `DeletePrototypeDialog`. They are not rebuilt here and
 * not wrapped: the upload dialog owns an abort controller and a five-phase
 * progress model, and the delete dialog owns a type-the-name confirmation whose
 * wording spells out the cascade. Reimplementing either for the viewer would
 * mean two versions of a destructive confirmation drifting apart.
 *
 * This component only names the actions; the shell owns which dialog is open,
 * because deleting has to navigate afterwards and a menu should not know about
 * routing.
 */
export const ViewerMenu = ({
  prototypeName,
  canUpload,
  canModify,
  onEdit,
  onUpload,
  onDelete,
}: ViewerMenuProps) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className="size-8 text-viewer-chrome-foreground hover:text-foreground"
      >
        <MoreHorizontal aria-hidden="true" />
        <span className="sr-only">Actions for {prototypeName}</span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-52">
      {canUpload ? (
        <DropdownMenuItem onSelect={onUpload}>
          <Upload aria-hidden="true" />
          Upload a new version
        </DropdownMenuItem>
      ) : null}
      {canUpload && canModify ? <DropdownMenuSeparator /> : null}
      {canModify ? (
        <>
          <DropdownMenuItem onSelect={onEdit}>
            <Pencil aria-hidden="true" />
            Edit prototype
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={onDelete}
          >
            <Trash2 aria-hidden="true" />
            Delete prototype
          </DropdownMenuItem>
        </>
      ) : null}
    </DropdownMenuContent>
  </DropdownMenu>
);
